// @flow

//#region imports
import React, { Component } from 'react';
import { message, Layout, Spin, Button, Affix, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import 'antd/dist/antd.css';
import './style/App.css';
import { UserContext } from './providers/UserProvider';

import Sidebar from './Sidebar';
import Spinner from './Spinner';
import {
    PdfLoader,
    PdfHighlighter,
    Tip,
    Highlight,
    Popup,
    AreaHighlight
} from '../../src';
import {
    DEFAULT_URL,
    FILE_PATH,
    PageMode,
    setPageMode,
    IsPageDirty,
    setIsPageDirty,
    IsPageLoading,
    setIsPageLoading,
    RelativePath
} from './GlobalParams';
import { firebaseObj, getTime } from './firebase';
import { dataService } from './dataService';
//#endregion

//#region Types
import type { T_Highlight, T_NewHighlight } from '../../src/types';

type T_ManuscriptHighlight = T_Highlight;
type State = {
    highlights: Array<T_ManuscriptHighlight>
};
//#endregion

//#region Global Members
const { Header, Footer, Sider, Content } = Layout;

const getNextId = () => String(Math.random()).slice(2);
const parseIdFromHash = () => location.hash.slice('#highlight-'.length);
const updateHash = (highlight) => {
    location.hash = `highlight-${highlight.id}`;
};
let AppInstance = null;
let firestore = null;

const HighlightPopup = ({ hideTip, comment, id, position, content }) => {
    return PageMode == 'ReadOnly' ? (
        <div className="Highlight__popup">
            <Button
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => {
                    AppInstance.removeHighlight(id, position, content);
                    hideTip();
                }}
            />
        </div>
    ) : (
        <div />
    );
};

const resetHash = () => {
    location.hash = '';
};

let hideMsg = null;
//#endregion

export default class DocumentViewer extends Component<Props, State> {
    state: State;

    state = {
        highlights: [],
        selectedHighlightId: parseIdFromHash(),
        isGetDataLoading: false,
        fileUrl: null,
        fileLoadedSize: 0,
        fileSize: 0
    };

    constructor(prop) {
        super(prop);
    }

    //#region highlight operations

    scrollViewerTo = (highlight: any) => {};
    hideViewerTipAndSelection = () => {};

    scrollToHighlightFromHash = () => {
        let hashId = parseIdFromHash();
        const highlight = this.getHighlightById(hashId);
        if (highlight) {
            this.scrollViewerTo(highlight);
        } else {
            AppInstance.hideViewerTipAndSelection();
        }
    };

    getHighlightById(id: string) {
        const { highlights } = this.state;
        return highlights.find((highlight) => highlight.id === id);
    }

    addHighlight(highlight: T_NewHighlight) {
        const { highlights, selectedHighlightId } = this.state;
        var isExist = highlights.some(function (item) {
            return item.id === selectedHighlightId;
        });
        if (selectedHighlightId && isExist) {
            this.setState({
                highlights: highlights.map((h) => {
                    return h.id === selectedHighlightId
                        ? {
                              ...h,
                              position: {
                                  ...h.position,
                                  ...highlight.position
                              },
                              content: { ...h.content, ...highlight.content }
                          }
                        : h;
                })
            });
        } else {
            let highlightId = getNextId();
            this.setState({
                highlights: [{ ...highlight, id: highlightId }, ...highlights]
            });
            updateHash({ id: highlightId });
            this.setState({ selectedHighlightId: highlightId });
        }
        setPageMode('ReadOnly');
        setIsPageDirty(true);
    }

    removeHighlight(highlightId: string, position: Object, content: Object) {
        this.setState({
            highlights: this.state.highlights.map((h) => {
                if (h.id === highlightId) {
                    h.content = {
                        image:
                            'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA='
                    };
                    h.position = { pageNumber: -1 };
                }
                return h;
            })
        });
        setIsPageDirty(true);
    }

    updateHighlight(highlightId: string, position: Object, content: Object) {
        this.setState({
            highlights: this.state.highlights.map((h) => {
                return h.id === highlightId
                    ? {
                          ...h,
                          position: { ...h.position, ...position },
                          content: { ...h.content, ...content }
                      }
                    : h;
            })
        });
        setIsPageDirty(true);
    }

    updateHighlightItem(highlightId: string, dataProp: string, value: string) {
        const { highlights } = AppInstance.state;
        AppInstance.setStatef({
            highlights: highlights.map((h) => {
                if (h.id === highlightId) h[dataProp] = value;
                return h;
            })
        });
        setIsPageDirty(true);
    }

    setHighlights(result: Array<T_ManuscriptHighlight>) {
        setPageMode('ReadOnly');
        setIsPageDirty(false);
        AppInstance.setState({ highlights: result, selectedHighlightId: '' });
        location.hash = '';
    }

    importHighlights(result: Array<T_ManuscriptHighlight>) {
        setPageMode('ReadOnly');
        let highlights = AppInstance.state.highlights || [];
        var oldIds = new Set(highlights.map((d) => d.id));
        var newIds = new Set(result.map((d) => d.id));
        let newHighlights = [
            ...result.filter((d) => !oldIds.has(d.id)),
            ...highlights
        ];
        newHighlights = newHighlights.map((h) => {
            if (newIds.has(h.id)) {
                let item = result.find((x) => x.id == h.id);
                h.plateNo = item.plateNo;
                h.partNo = item.partNo;
                h.eqNo = item.eqNo;
                h.eqName = item.eqName;
                h.itemDesc = item.itemDesc;
                h.itemSpec = item.itemSpec;
                h.positionNo = item.positionNo;
                h.importPageNo = item.importPageNo;
            }
            return h;
        });
        AppInstance.setState({
            highlights: newHighlights,
            selectedHighlightId: ''
        });
        location.hash = '';
    }

    setSelectedHighlightId(id: String) {
        const { highlights, selectedHighlightId } = AppInstance.state;
        if (selectedHighlightId != id) {
            if (id) {
                var selectedObj = highlights.find((x) => x.id == id);
                if (selectedObj) {
                    let newItemEditMode =
                        selectedObj.position &&
                        selectedObj.position.boundingRect
                            ? false
                            : true;
                    if (newItemEditMode) {
                        setPageMode('Update');
                        hideMsg ? hideMsg() : null;
                        hideMsg = message.info(
                            'Please mark the area for ' + selectedObj.itemDesc
                        );
                    } else {
                        if (PageMode != 'ReadOnly') setPageMode('ReadOnly');
                    }
                } else {
                    if (PageMode != 'Add') setPageMode('Add');
                }
            } else {
                if (PageMode != 'ReadOnly') setPageMode('ReadOnly');
            }
            AppInstance.setState({ selectedHighlightId: id });
            updateHash({ id: id });
        }
    }
    //#endregion

    render() {
        const { highlights, selectedHighlightId } = this.state;

        if (DEFAULT_URL) {
            return (
                <div>
                    <Spin tip="Please wait..." spinning={IsPageLoading}>
                        <Layout>
                            <Content>
                                <div
                                    style={{
                                        height: '100vh',
                                        position: 'relative'
                                    }}
                                >
                                    {this.state.fileUrl && (
                                        <PdfLoader
                                            url={this.state.fileUrl}
                                            beforeLoad={
                                                <Spinner
                                                    tip={`${
                                                        (
                                                            this.state
                                                                .fileLoadedSize /
                                                            this.state.fileSize
                                                        ).toFixed() * 100
                                                    }% Loaded`}
                                                />
                                            }
                                            progressCallback={(data) => {
                                                this.setState({
                                                    fileLoadedSize: data.loaded
                                                });
                                            }}
                                        >
                                            {(pdfDocument) => (
                                                <PdfHighlighter
                                                    pdfDocument={pdfDocument}
                                                    enableAreaSelection={(
                                                        event
                                                    ) => PageMode != 'ReadOnly'}
                                                    onScrollChange={resetHash}
                                                    scrollRef={(scrollTo) => {
                                                        this.scrollViewerTo = scrollTo;
                                                        this.scrollToHighlightFromHash();
                                                    }}
                                                    onSelectionFinished={(
                                                        position,
                                                        content,
                                                        hideTipAndSelection,
                                                        transformSelection
                                                    ) => {
                                                        const isAreaHighlight = Boolean(
                                                            position &&
                                                                position.boundingRect
                                                        );
                                                        return isAreaHighlight ? (
                                                            <Tip
                                                                onClose={
                                                                    hideTipAndSelection
                                                                }
                                                                onOpen={(
                                                                    transformSelection
                                                                ) => {
                                                                    this.hideViewerTipAndSelection = hideTipAndSelection;
                                                                }}
                                                                onConfirm={(
                                                                    comment
                                                                ) => {
                                                                    this.addHighlight(
                                                                        {
                                                                            content,
                                                                            position,
                                                                            comment
                                                                        }
                                                                    );
                                                                    hideTipAndSelection();
                                                                }}
                                                            />
                                                        ) : (
                                                            <div />
                                                        );
                                                    }}
                                                    highlightTransform={(
                                                        highlight,
                                                        index,
                                                        setTip,
                                                        hideTip,
                                                        viewportToScaled,
                                                        screenshot,
                                                        isScrolledTo
                                                    ) => {
                                                        const isTextHighlight = !Boolean(
                                                            highlight.position &&
                                                                highlight
                                                                    .position
                                                                    .boundingRect
                                                        );

                                                        const component = isTextHighlight ? (
                                                            <div />
                                                        ) : (
                                                            <AreaHighlight
                                                                highlight={
                                                                    highlight
                                                                }
                                                                selectedHighlightId={
                                                                    selectedHighlightId
                                                                }
                                                                onClick={(
                                                                    id
                                                                ) => {
                                                                    this.setSelectedHighlightId(
                                                                        id
                                                                    );
                                                                }}
                                                                onChange={(
                                                                    boundingRect
                                                                ) => {
                                                                    this.updateHighlight(
                                                                        highlight.id,
                                                                        {
                                                                            boundingRect: viewportToScaled(
                                                                                boundingRect
                                                                            )
                                                                        },
                                                                        {
                                                                            image: screenshot(
                                                                                boundingRect
                                                                            )
                                                                        }
                                                                    );
                                                                }}
                                                            />
                                                        );

                                                        return (
                                                            <Popup
                                                                popupContent={
                                                                    <HighlightPopup
                                                                        hideTip={
                                                                            hideTip
                                                                        }
                                                                        {...highlight}
                                                                    />
                                                                }
                                                                onMouseOver={(
                                                                    popupContent
                                                                ) =>
                                                                    setTip(
                                                                        highlight,
                                                                        (
                                                                            highlight
                                                                        ) =>
                                                                            popupContent
                                                                    )
                                                                }
                                                                onMouseOut={
                                                                    hideTip
                                                                }
                                                                key={index}
                                                                children={
                                                                    component
                                                                }
                                                            />
                                                        );
                                                    }}
                                                    highlights={highlights}
                                                />
                                            )}
                                        </PdfLoader>
                                    )}
                                </div>
                            </Content>
                            <Sider theme="light" width={350}>
                                <Sidebar
                                    highlights={highlights}
                                    importHighlights={this.importHighlights}
                                    selectedHighlightId={selectedHighlightId}
                                    fileId={this.state.fileId}
                                    fileRef={this.state.fileRef}
                                    setSelectedHighlightId={
                                        this.setSelectedHighlightId
                                    }
                                    reloadData={this.loadData}
                                    afterSave={this.afterSave}
                                    updateHighlightItem={
                                        this.updateHighlightItem
                                    }
                                    isGetDataLoading={
                                        this.state.isGetDataLoading
                                    }
                                    setIsGetDataLoading={(val) => {
                                        this.setState({
                                            isGetDataLoading: val
                                        });
                                    }}
                                />
                            </Sider>
                        </Layout>
                    </Spin>
                </div>
            );
        } else {
            return <div style={{ color: 'black' }}>Not found!</div>;
        }
    }

    afterSave() {
        setPageMode('ReadOnly');
        setIsPageDirty(false);
        AppInstance.setState({
            highlights: AppInstance.state.highlights,
            selectedHighlightId: ''
        });
        location.hash = '';
        document.title = document.title.replace('*', '');
    }

    loadData() {
        AppInstance.setState({ isGetDataLoading: true });
        dataService.firestore
            .collection('docMarkers')
            .where('fileId', '==', AppInstance.state.fileId)
            .get()
            .then((data) => {
                AppInstance.setState({ isGetDataLoading: false });
                if (data) AppInstance.setHighlights(data);
            });
    }

    componentDidMount() {
        AppInstance = this;
        this.init();
        window.addEventListener(
            'hashchange',
            this.scrollToHighlightFromHash,
            false
        );
    }

    async init(params) {
        let user = JSON.parse(localStorage.getItem('user'));
        firestore = dataService.firestore;
        const fileRef = await firestore
            .collection('files')
            .doc(decodeURIComponent(DEFAULT_URL));
        const snapshot = await fileRef.get();
        const item = snapshot.data();
        item.lastAccessedOn = getTime();
        item.lastAccessedBy = user.uid;
        var newfileRef = firestore.collection('files').doc(item.key);
        await newfileRef.set(item);
        this.setState({
            fileUrl: item.downloadURL,
            fileSize: item.size,
            fileId: item.id,
            fileRef: item
        });
        this.loadData();
    }
}



// WEBPACK FOOTER //
// ./demo/src/DocumentViewer.js